/**
 * Formats conversation last-message previews for lists/modals.
 * Typed payloads like `CONTACTS: {...}` become a short label instead of raw JSON.
 */
export function formatLastMessagePreview(
  msg: string | undefined | null,
  translate: (key: string) => string,
  maxLen = 40,
): string {
  const raw = (msg || "").trim();
  if (!raw) {
    return "-";
  }

  const type = raw.split(":")[0]?.trim().toUpperCase();
  switch (type) {
    case "CONTACTS":
      return translate("TEAM_INBOX.CONVERSATION.CONTACT");
    case "LOCATION":
      return translate("TEAM_INBOX.CONVERSATION.LOCATION");
    case "IMAGE":
      return translate("TEAM_INBOX.CONVERSATION.IMAGE");
    case "AUDIO":
      return translate("TEAM_INBOX.CONVERSATION.AUDIO");
    case "VIDEO":
      return translate("TEAM_INBOX.CONVERSATION.VIDEO");
    case "DOCUMENT":
      return translate("TEAM_INBOX.CONVERSATION.DOCUMENT");
    case "STICKER":
      return translate("TEAM_INBOX.CONVERSATION.STICKER");
    case "INTERACTIVE": {
      const separatorIndex = raw.indexOf(":");
      const content =
        separatorIndex >= 0 ? raw.slice(separatorIndex + 1).trim() : "";
      if (!content || content.startsWith("{") || content.startsWith("[")) {
        return translate("FLOW_META.PAGE.RESPONSE");
      }
      const decodedReplyCode = decodeInteractiveReplyCode(content, translate);
      if (decodedReplyCode) {
        return decodedReplyCode;
      }
      const cleaned = content.replace(/\s+/g, " ").trim();
      return cleaned.length <= maxLen
        ? cleaned
        : `${cleaned.slice(0, maxLen).trimEnd()}…`;
    }
    case "ORDER":
      return translate("ORDER_META.PAGE.RESPONSE");
    case "REACTION": {
      const content = raw.slice(raw.indexOf(":") + 1).trim();
      return `${translate("TEAM_INBOX.CONVERSATION.REACTION_MASSAGE")} ${content}`.trim();
    }
    default:
      break;
  }

  const decodedReplyCode = decodeInteractiveReplyCode(raw, translate);
  if (decodedReplyCode) {
    return decodedReplyCode;
  }

  // Avoid dumping JSON blobs in previews.
  if (raw.startsWith("{") || raw.startsWith("[")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_MESSAGE");
  }

  const cleaned = raw.replace(/\s+/g, " ").trim();
  if (cleaned.length <= maxLen) {
    return cleaned;
  }
  return `${cleaned.slice(0, maxLen).trimEnd()}…`;
}

/**
 * Decodes the raw button/list-reply payloads carousels send back
 * (e.g. `CAT:110:4687828735`) into a human-readable label. Returns
 * `undefined` when `raw` isn't one of these internal codes, so callers
 * can fall back to showing the content as-is.
 */
export function decodeInteractiveReplyCode(
  raw: string | undefined | null,
  translate: (key: string) => string,
): string | undefined {
  const value = (raw || "").trim();
  if (!value) {
    return undefined;
  }
  const upper = value.toUpperCase();
  if (upper.startsWith("PAYMENT:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_PAYMENT_CHOICE");
  }
  if (upper.startsWith("DELIVERY:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_DELIVERY_CHOICE");
  }
  if (upper.startsWith("PROD:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_PRODUCT_CHOICE");
  }
  if (upper.startsWith("CAT:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_CATEGORY_CHOICE");
  }
  if (upper.startsWith("OPT:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_OPTION_CHOICE");
  }
  if (upper.startsWith("ROOM:")) {
    return translate("TEAM_INBOX.CONVERSATION.INTERACTIVE_ROOM_CHOICE");
  }
  if (
    upper.startsWith("PROD_CONFIRM:") ||
    upper.startsWith("ORDER_CONFIRM:") ||
    upper.startsWith("ADDR_CONFIRM:")
  ) {
    return translate(
      upper.endsWith(":NO")
        ? "TEAM_INBOX.CONVERSATION.INTERACTIVE_NO"
        : "TEAM_INBOX.CONVERSATION.INTERACTIVE_YES",
    );
  }
  return undefined;
}
